// src/hooks/useNavigationGuard.js
import React from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { useDialog } from "context/DialogContext";

/**
 * Guards application navigation and browser exit while local changes are dirty.
 * Saving must succeed before navigation proceeds.
 */
export function useNavigationGuard(
  shouldBlock,
  onSave,
  onDiscard,
  message,
  options = {}
) {
  const { navigator } = React.useContext(UNSAFE_NavigationContext);
  const { showDialog } = useDialog();
  const {
    title = "Save Changes?",
    saveLabel = "Save",
    discardLabel = "Discard",
    cancelLabel = "Cancel",
    canSave = true,
  } = options;

  React.useEffect(() => {
    if (!shouldBlock) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlock]);

  React.useEffect(() => {
    if (!shouldBlock) return undefined;

    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    const guardedNavigate = (navigateMethod) => async (...args) => {
      if (window.localStorage.getItem("compass.inactivityLogout")) {
        navigateMethod.apply(navigator, args);
        return;
      }

      const result = await showDialog({
        title,
        message:
          message ||
          "You have unsaved changes. Do you want to save before leaving?",
        buttons: canSave
          ? [
              { label: saveLabel, value: "save", variant: "primary" },
              { label: discardLabel, value: "discard", variant: "ghost" },
              { label: cancelLabel, value: "cancel", variant: "brand-ghost" },
            ]
          : [
              { label: discardLabel, value: "discard", variant: "primary" },
              { label: cancelLabel, value: "cancel", variant: "ghost" },
            ],
        defaultAction: canSave ? "save" : "cancel",
      });

      if (result === "save") {
        const saveResult = await Promise.resolve(onSave?.());
        if (saveResult === false || saveResult?.ok === false) return;
        navigateMethod.apply(navigator, args);
        return;
      }

      if (result === "discard") {
        await Promise.resolve(onDiscard?.());
        navigateMethod.apply(navigator, args);
      }
    };

    navigator.push = guardedNavigate(originalPush);
    if (originalReplace) navigator.replace = guardedNavigate(originalReplace);

    return () => {
      navigator.push = originalPush;
      if (originalReplace) navigator.replace = originalReplace;
    };
  }, [
    cancelLabel,
    canSave,
    discardLabel,
    message,
    navigator,
    onDiscard,
    onSave,
    saveLabel,
    shouldBlock,
    showDialog,
    title,
  ]);
}
